import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { presentationUserFacingError } from "../../utils/presentationUserFacingError";

const PRESENTATION_PIPELINE_STEPS = (t) => [
  { key: "queued", label: t("workspaceCreate.progress.queued", { defaultValue: "Na fila…" }) },
  { key: "scraping", label: t("workspaceCreate.progress.scraping", { defaultValue: "Coletando informações do site" }) },
  {
    key: "generating",
    label: t("workspaceCreate.progress.generating", { defaultValue: "Gerando dados de demonstração" }),
  },
  { key: "applying", label: t("workspaceCreate.progress.applying", { defaultValue: "Aplicando no workspace" }) },
  { key: "ready", label: t("workspaceCreate.progress.ready", { defaultValue: "Pronto!" }) },
];

function bannerStatusStepIndex(status) {
  const s = String(status || "queued").toLowerCase();
  const order = ["queued", "scraping", "generating", "applying", "ready"];
  const i = order.indexOf(s);
  if (i >= 0) return i;
  if (s === "converting") return 3;
  return 0;
}

function ConvertDialog({ open, mode, onClose, onConfirm, planItems, busy }) {
  const { t } = useTranslation("app");
  const [planSku, setPlanSku] = useState("");
  const [planVersion, setPlanVersion] = useState(1);

  useEffect(() => {
    if (!open) {
      setPlanSku("");
      setPlanVersion(1);
    }
  }, [open]);

  const title =
    mode === "pay"
      ? t("partnerConsole.presentation.convertDialog.titlePay", { defaultValue: "Converter para Pagamento" })
      : t("partnerConsole.presentation.convertDialog.titleTrial", { defaultValue: "Converter para Trial" });

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t("partnerConsole.presentation.convertDialog.description", {
            defaultValue:
              "Os dados de demonstração serão removidos. O workspace será reinicializado com a estrutura limpa, preservando apenas as informações da empresa e os usuários reais.",
          })}
        </DialogContentText>
        <Stack direction="row" spacing={2}>
          <TextField
            select
            label={t("workspaceCreate.fields.plan", { defaultValue: "Plano" })}
            value={planSku}
            onChange={(e) => setPlanSku(e.target.value)}
            size="small"
            sx={{ flex: 2 }}
            helperText={
              mode === "pay"
                ? t("workspaceCreate.fields.planRequiredForPay", {
                    defaultValue: "Obrigatório para o modo Pagamento.",
                  })
                : t("workspaceCreate.fields.planOptional", {
                    defaultValue: "Opcional. Mantém o plano de trial padrão se não informado.",
                  })
            }
          >
            <MenuItem value="">
              <em>{t("workspaceCreate.fields.planNone", { defaultValue: "Padrão" })}</em>
            </MenuItem>
            {(planItems || []).map((p) => (
              <MenuItem key={`${p.sku}-${p.version}`} value={p.sku}>
                {p.name || p.sku} — v{p.version}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={t("workspaceCreate.fields.planVersion", { defaultValue: "Versão" })}
            value={planVersion}
            onChange={(e) => setPlanVersion(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            inputProps={{ min: 1 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            onConfirm({
              mode,
              planSku: planSku || undefined,
              planVersion: Number(planVersion) || 1,
            })
          }
          disabled={busy || (mode === "pay" && !planSku)}
        >
          {busy
            ? t("common.processing", { defaultValue: "Processando…" })
            : t("partnerConsole.presentation.convertDialog.confirm", { defaultValue: "Confirmar conversão" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DiscardDialog({ open, onClose, onConfirm, busy }) {
  const { t } = useTranslation("app");
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("partnerConsole.presentation.discardDialog.title", { defaultValue: "Descartar workspace de apresentação?" })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t("partnerConsole.presentation.discardDialog.description", {
            defaultValue:
              "Remove definitivamente o workspace de apresentação: o banco do tenant é apagado, o registro na plataforma é excluído e o identificador volta a ficar disponível para reuso. Não pode ser desfeita.",
          })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={busy}>
          {busy
            ? t("common.processing", { defaultValue: "Processando…" })
            : t("partnerConsole.presentation.discardDialog.confirm", { defaultValue: "Confirmar descarte" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Banner reutilizável de workspace de apresentação para painéis revenda/master.
 * Faz polling do status enquanto a geração está em andamento e expõe ações de Conversão/Descarte.
 *
 * Props:
 *  - dominioId
 *  - presentation: payload inicial vindo do detalhe (status, source_url, ...)
 *  - planItems: catálogo de planos para o modal de conversão
 *  - getPresentationStatus(dominioId): polling
 *  - convertPresentation(dominioId, payload): conversão para trial/pay
 *  - discardPresentation(dominioId): descarte
 *  - onChange?: chamado após conversão/descarte para o pai recarregar
 *  - onDiscarded?: chamado após descarte para navegação
 */
const PresentationWorkspaceBanner = ({
  dominioId,
  presentation,
  planItems,
  getPresentationStatus,
  convertPresentation,
  discardPresentation,
  onChange,
  onDiscarded,
}) => {
  const { t } = useTranslation("app");
  const [livePresentation, setLivePresentation] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertMode, setConvertMode] = useState("trial");
  const [discardOpen, setDiscardOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [pollError, setPollError] = useState("");
  const [pollFailureCount, setPollFailureCount] = useState(0);
  const [staleQueuedHint, setStaleQueuedHint] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const pollStartedAtRef = useRef(null);

  const data = livePresentation || presentation || {};
  const status = String(data.status || "queued").toLowerCase();
  const isProcessing = ["queued", "scraping", "generating", "applying", "converting"].includes(status);
  const isReady = status === "ready";
  const isFailed = status === "failed";
  const pipeline = PRESENTATION_PIPELINE_STEPS(t);
  const safeIndex = bannerStatusStepIndex(status);
  const sourceDisplay = data.source_url || data.sourceUrl;
  const updatedAt = data.updated_at || data.updatedAt;

  const rawFailMsg = String(data.error_message || data.errorMessage || "").trim();
  const failDisplay = presentationUserFacingError(rawFailMsg, t);
  const failWasSanitized = Boolean(rawFailMsg) && failDisplay !== rawFailMsg;

  const shouldPoll = useMemo(
    () => isProcessing && Boolean(getPresentationStatus) && Boolean(dominioId),
    [isProcessing, getPresentationStatus, dominioId]
  );

  useEffect(() => {
    if (!shouldPoll) return undefined;
    pollStartedAtRef.current = Date.now();
    setPollError("");
    setPollFailureCount(0);
    setStaleQueuedHint(false);
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const res = await getPresentationStatus(dominioId);
        if (cancelled) return;
        setPollError("");
        setPollFailureCount(0);
        const next = res?.presentation || null;
        setLivePresentation(next);
        const s = String(next?.status || "").toLowerCase();
        const elapsed = pollStartedAtRef.current ? Date.now() - pollStartedAtRef.current : 0;
        const errText =
          (typeof next?.errorMessage === "string" && next.errorMessage.trim()) ||
          (typeof next?.error_message === "string" && next.error_message.trim()) ||
          "";
        if (s === "failed" || errText) setStaleQueuedHint(false);
        else if (s === "queued" && elapsed > 120_000) setStaleQueuedHint(true);
        else if (s !== "queued") setStaleQueuedHint(false);
        if (["ready", "failed", "discarded", "converted"].includes(s)) {
          if (typeof onChange === "function") onChange();
          return;
        }
      } catch (e) {
        if (!cancelled) {
          const msg =
            (typeof e?.response?.data?.message === "string" && e.response.data.message) ||
            e?.message ||
            t("workspaceCreate.progress.pollErrorGeneric", {
              defaultValue: "Não foi possível obter o status.",
            });
          setPollError(msg);
          setPollFailureCount((c) => c + 1);
        }
      }
      if (!cancelled) timer = setTimeout(tick, 4000);
    };
    timer = setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [shouldPoll, dominioId, getPresentationStatus, onChange, t]);

  const refreshStatus = useCallback(async () => {
    if (!dominioId || !getPresentationStatus) return;
    setRefreshBusy(true);
    setPollError("");
    setPollFailureCount(0);
    try {
      const res = await getPresentationStatus(dominioId);
      const next = res?.presentation || null;
      setLivePresentation(next);
      if (typeof onChange === "function") onChange();
    } catch (e) {
      const msg =
        (typeof e?.response?.data?.message === "string" && e.response.data.message) ||
        e?.message ||
        t("workspaceCreate.progress.pollErrorGeneric", { defaultValue: "Não foi possível obter o status." });
      setPollError(msg);
    } finally {
      setRefreshBusy(false);
    }
  }, [dominioId, getPresentationStatus, onChange, t]);

  const handleConvert = useCallback(
    async ({ mode, planSku, planVersion }) => {
      if (typeof convertPresentation !== "function") return;
      setBusy(true);
      setFeedback(null);
      try {
        const payload = { mode };
        if (planSku) payload.planSku = planSku;
        if (planVersion) payload.planVersion = planVersion;
        await convertPresentation(dominioId, payload);
        setFeedback({
          severity: "success",
          text: t("partnerConsole.presentation.convertSuccess", {
            defaultValue: "Workspace convertido com sucesso.",
          }),
        });
        setConvertOpen(false);
        if (typeof onChange === "function") onChange();
      } catch (err) {
        setFeedback({
          severity: "error",
          text:
            err?.response?.data?.message ||
            err?.message ||
            t("partnerConsole.presentation.convertError", { defaultValue: "Falha ao converter workspace." }),
        });
      } finally {
        setBusy(false);
      }
    },
    [convertPresentation, dominioId, onChange, t]
  );

  const handleDiscard = useCallback(async () => {
    if (typeof discardPresentation !== "function") return;
    setBusy(true);
    setFeedback(null);
    try {
      await discardPresentation(dominioId);
      setDiscardOpen(false);
      if (typeof onDiscarded === "function") onDiscarded();
      else if (typeof onChange === "function") onChange();
    } catch (err) {
      setFeedback({
        severity: "error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          t("partnerConsole.presentation.discardError", { defaultValue: "Falha ao descartar workspace." }),
      });
    } finally {
      setBusy(false);
    }
  }, [discardPresentation, dominioId, onChange, onDiscarded, t]);

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: { xs: 2, sm: 2.5 },
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.12)} 0%, ${alpha(theme.palette.warning.main, 0.04)} 100%)`,
      })}
    >
      {feedback ? (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.text}
        </Alert>
      ) : null}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }} justifyContent="space-between">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <AutoAwesomeIcon color="warning" />
            <Typography variant="h6" fontWeight={700}>
              {t("partnerConsole.presentation.bannerTitle", { defaultValue: "Workspace de apresentação" })}
            </Typography>
            <Chip
              size="small"
              label={t(`partnerConsole.presentation.status.${status}`, { defaultValue: status })}
              color={isFailed ? "error" : isReady ? "success" : "warning"}
              variant={isReady ? "filled" : "outlined"}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t("partnerConsole.presentation.bannerDescription", {
              defaultValue:
                "Este workspace contém dados fictícios gerados por IA para demonstração. Converta para Trial ou Pago para começar a usar com dados reais.",
            })}
          </Typography>
          {sourceDisplay ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {t("partnerConsole.presentation.sourceLabel", { defaultValue: "Site de origem:" })} {sourceDisplay}
            </Typography>
          ) : null}

          {pollError ? (
            <Alert
              severity={pollFailureCount >= 1 ? "error" : "warning"}
              sx={{ mt: 2 }}
              onClose={() => {
                setPollError("");
                setPollFailureCount(0);
              }}
            >
              {pollError}
            </Alert>
          ) : null}

          {staleQueuedHint && isProcessing && !isFailed && !pollError ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t("workspaceCreate.progress.staleQueued", {
                defaultValue:
                  "A preparação está na fila há mais tempo que o habitual — às vezes o serviço está mais lento. Toque em Atualizar status ou aguarde alguns minutos. Se não mudar, abra os detalhes deste workspace ou fale com o suporte informando o número acima.",
              })}
            </Alert>
          ) : null}

          {isProcessing ? (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Button type="button" size="small" variant="outlined" disabled={refreshBusy} onClick={refreshStatus}>
                  {refreshBusy
                    ? t("workspaceCreate.progress.refreshing", { defaultValue: "Atualizando…" })
                    : t("workspaceCreate.progress.refreshStatus", { defaultValue: "Atualizar status" })}
                </Button>
                {updatedAt ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("workspaceCreate.progress.lastServerUpdate", {
                      defaultValue: "Última atualização no servidor: {{time}}",
                      time: String(updatedAt),
                    })}
                  </Typography>
                ) : null}
              </Stack>
              <LinearProgress sx={{ mb: 1 }} />
              <Stepper activeStep={safeIndex} alternativeLabel>
                {pipeline.map((s) => (
                  <Step key={s.key}>
                    <StepLabel>{s.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          ) : null}

          {isFailed ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t("partnerConsole.presentation.failedHint", {
                defaultValue:
                  "Não foi possível gerar a demonstração automática. Você pode descartar este workspace e criar outro, ou pedir ajuda ao suporte.",
              })}
              {failDisplay ? (
                failWasSanitized ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {failDisplay}
                  </Typography>
                ) : (
                  <Box sx={{ mt: 1, fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap" }}>
                    {failDisplay}
                  </Box>
                )
              ) : null}
            </Alert>
          ) : null}
        </Box>

        <Stack direction="column" spacing={1} sx={{ flexShrink: 0, width: { xs: 1, sm: 220 } }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={busy || !isReady || !convertPresentation}
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={() => {
              setConvertMode("trial");
              setConvertOpen(true);
            }}
          >
            {t("partnerConsole.presentation.convertToTrial", { defaultValue: "Converter para Trial" })}
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            disabled={busy || !isReady || !convertPresentation}
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={() => {
              setConvertMode("pay");
              setConvertOpen(true);
            }}
          >
            {t("partnerConsole.presentation.convertToPay", { defaultValue: "Converter para Pago" })}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={busy || !discardPresentation}
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={() => setDiscardOpen(true)}
          >
            {t("partnerConsole.presentation.discard", { defaultValue: "Descartar workspace" })}
          </Button>
        </Stack>
      </Stack>

      <ConvertDialog
        open={convertOpen}
        mode={convertMode}
        onClose={() => setConvertOpen(false)}
        onConfirm={handleConvert}
        planItems={planItems}
        busy={busy}
      />
      <DiscardDialog open={discardOpen} onClose={() => setDiscardOpen(false)} onConfirm={handleDiscard} busy={busy} />
    </Paper>
  );
};

export default PresentationWorkspaceBanner;
